import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { bindActionCreators } from "redux";
import { useDispatch } from "react-redux";
import {
    ITrainingVideosResponse,
    MarkWatchedActionPayload,
    TrainingVideoState,
} from "../types/training-video";

const initialState: TrainingVideoState = {
    data: [],
    meta: undefined,
    loading: false,
    loaded: false,
    error: false,
    forbidden: false,
    watchingId: null,
    watchEpoch: 0,
    getEpoch: 0,
};

const slice = createSlice({
    name: "trainingVideo",
    initialState,
    reducers: {
        getTrainingVideos(state) {
            state.loading = true;
            state.error = false;
            state.forbidden = false;
            // Snapshot the watch epoch so a response that arrives after a watch
            // has landed can be recognised as stale. See getTrainingVideosSuccess.
            state.getEpoch = state.watchEpoch;
        },
        getTrainingVideosSuccess(state, action: PayloadAction<ITrainingVideosResponse>) {
            state.loading = false;
            state.loaded = true;
            state.error = false;
            state.forbidden = false;

            // A watch landed while this GET was in flight. Its response already
            // carried the newer state for both surfaces, so applying this older
            // snapshot would silently un-check the video the user just marked.
            if (state.getEpoch !== state.watchEpoch) return;

            // Coalesce data so a shape change upstream degrades to an empty
            // library rather than a crash on data.length.
            state.data = action.payload?.data ?? [];
            state.meta = action.payload?.meta;
        },
        getTrainingVideosFailure(state, action: PayloadAction<{ forbidden: boolean }>) {
            // Keep any already-loaded data so a failed refetch doesn't blank the
            // banner or card out from under an open modal.
            state.loading = false;
            state.error = true;
            state.forbidden = action.payload?.forbidden ?? false;
        },

        markWatched(state, action: MarkWatchedActionPayload) {
            state.watchingId = action.payload;
        },
        markWatchedSuccess(state, action: PayloadAction<ITrainingVideosResponse>) {
            // The watch response is the new state for both surfaces — no second GET.
            state.data = action.payload?.data ?? [];
            state.meta = action.payload?.meta;
            state.loaded = true;
            state.watchingId = null;
            state.watchEpoch += 1;
        },
        markWatchedFailure(state, action: PayloadAction<{ forbidden: boolean }>) {
            state.watchingId = null;
            state.forbidden = action.payload?.forbidden ?? false;
        },
    },
});

export const {
    getTrainingVideos,
    getTrainingVideosSuccess,
    getTrainingVideosFailure,
    markWatched,
    markWatchedSuccess,
    markWatchedFailure,
} = slice.actions;

export const mapDispatchToProps = () => {
    return bindActionCreators({ getTrainingVideos, markWatched }, useDispatch());
};

export default slice.reducer;
